"use client";

import { useRef, useState } from "react";
import { PrintButton, PrintFooter, trackToolResult } from "@/components/tools/ui";
import { track } from "@/lib/analytics";
import { CheckResultView } from "@/components/tools/CheckResultView";
import { CheckProgress, ReviewingDots } from "@/components/tools/CheckProgress";
import {
  countFindings,
  runRamsCheck,
  DIMENSION_LABELS,
  type CheckResult,
} from "@/lib/ramsCheck";

/* AI RAMS rejection checker. The user pastes (or PDF-extracts) their actual
   document and it's reviewed the way a principal contractor's document
   controller reviews on first pass: scored on eight dimensions with
   severity-ranked findings. The /api/rams-check worker streams an Anthropic
   SSE response whose accumulated content_block_delta text forms one JSON
   object matching CheckResult. Error responses are non-streaming JSON.

   The result shape, parse/derive/count helpers, the eight dimension labels and
   the fetch+stream call live in lib/ramsCheck.ts; the scorecard rendering lives
   in components/tools/CheckResultView.tsx — both shared with the in-builder
   review (components/BuilderReview.tsx). */

const MIN_CHARS = 400;
const MAX_CHARS = 120000;
const MAX_PDF_BYTES = 15 * 1024 * 1024;

export function RamsRejectionChecker() {
  const [text, setText] = useState("");
  const [reviewerComments, setReviewerComments] = useState("");
  const [gatekeeper, setGatekeeper] = useState("");
  const [trimmed, setTrimmed] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [reading, setReading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [dimsArrived, setDimsArrived] = useState(0);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const charCount = text.length;
  const tooShort = charCount < MIN_CHARS;
  const busy = streaming || reading;

  async function captureReviewerFeedback() {
    const comments = reviewerComments.trim();
    if (!comments) return;
    try {
      const form = new FormData();
      form.set("comments", comments.substring(0, 4000));
      form.set("gatekeeper", gatekeeper.trim().substring(0, 120));
      form.set("report", "standalone-checker");
      await fetch("/api/feedback", { method: "POST", body: form });
    } catch {
      /* best-effort flywheel capture; checker result still matters */
    }
  }

  function applyText(raw: string) {
    if (raw.length > MAX_CHARS) {
      setText(raw.slice(0, MAX_CHARS));
      setTrimmed(true);
    } else {
      setText(raw);
      setTrimmed(false);
    }
  }

  async function handleFile(file: File) {
    setPdfError("");
    if (file.type !== "application/pdf") {
      setPdfError("That isn't a PDF — paste the text instead, or choose a PDF file.");
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setPdfError("That PDF is over 15MB — open it and copy/paste the text instead.");
      return;
    }
    setReading(true);
    try {
      const pdfjs = await import("pdfjs-dist");
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url,
      ).toString();
      const data = await file.arrayBuffer();
      const doc = await pdfjs.getDocument({ data }).promise;
      const pages: string[] = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const content = await page.getTextContent();
        const strings = content.items
          .map((item) => ("str" in item ? item.str : ""))
          .filter(Boolean);
        pages.push(strings.join(" "));
      }
      const extracted = pages.join("\n").trim();
      if (!extracted) {
        setPdfError("Couldn't read that PDF — open it and copy/paste the text instead.");
      } else {
        applyText(extracted);
      }
    } catch {
      setPdfError("Couldn't read that PDF — open it and copy/paste the text instead.");
    } finally {
      setReading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function review() {
    if (tooShort || busy) return;
    setError("");
    setResult(null);
    setDimsArrived(0);
    setStreaming(true);
    const hasReviewerComments = reviewerComments.trim().length > 0;
    if (hasReviewerComments) {
      track("rejection_flow_started", { tool: "rams-rejection-checker", gatekeeper: gatekeeper.trim().substring(0, 120) });
      track("rejection_feedback_pasted", {
        tool: "rams-rejection-checker",
        gatekeeper: gatekeeper.trim().substring(0, 120),
        comments_length: reviewerComments.trim().length,
      });
      trackToolResult("rams-rejection-checker", {
        action: "rejection_feedback_pasted",
        gatekeeper: gatekeeper.trim().substring(0, 120),
        comments_length: reviewerComments.trim().length,
      });
      void captureReviewerFeedback();
    }

    try {
      const reviewText = hasReviewerComments
        ? [
            "PRINCIPAL CONTRACTOR / CLIENT REVIEWER COMMENTS PROVIDED BY THE USER:",
            reviewerComments.trim().substring(0, 4000),
            "",
            "RAMS DOCUMENT TO REVIEW:",
            text,
          ].join("\n")
        : text;
      const parsed = await runRamsCheck(reviewText, (json) => {
        // Each dimension object opens with a "key" property — counting them
        // shows progress through the eight checks as they arrive.
        const matches = json.match(/"key"/g);
        if (matches) setDimsArrived(Math.min(matches.length, DIMENSION_LABELS.length));
      });
      setResult(parsed);

      const counts = countFindings(parsed.dimensions);
      if (hasReviewerComments) {
        track("rejection_fix_generated", {
          verdict: parsed.verdict,
          critical: counts.critical,
          major: counts.major,
          minor: counts.minor,
        });
      }
      trackToolResult("rams-rejection-checker", {
        action: hasReviewerComments ? "rejection_fix_reviewed" : "pre_submission_reviewed",
        verdict: parsed.verdict,
        critical: counts.critical,
        major: counts.major,
        minor: counts.minor,
        has_reviewer_comments: hasReviewerComments,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't reach the checker — check your connection and try again.");
    } finally {
      setStreaming(false);
    }
  }

  function reset() {
    setText("");
    setTrimmed(false);
    setPdfError("");
    setResult(null);
    setError("");
    setDimsArrived(0);
  }

  // ── Results view ─────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="space-y-4">
        <div className="print-area card shadow-soft p-6">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
            RAMS pre-submission review
          </div>
          <h2 className="mt-1 text-lg">
            {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </h2>

          <div className="mt-4">
            <CheckResultView result={result} />
          </div>

          <PrintFooter toolName="RAMS rejection checker" />
        </div>

        <div className="flex flex-wrap justify-between gap-3 print-hide">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-full border border-line-2 px-6 py-3 text-[0.95rem] font-semibold text-ink transition-all hover:border-ink hover:bg-mist"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" />
            </svg>
            Check another document
          </button>
          <PrintButton tool="rams-rejection-checker" label="Print / save report as PDF" />
        </div>
      </div>
    );
  }

  // ── Input view ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="card shadow-soft p-6 print-hide">
        <h2 className="text-xl">Paste your RAMS, or upload the PDF</h2>
        <p className="mt-1 text-sm text-muted">
          Paste the full text of the document you&apos;re about to submit. It gets reviewed on the eight checks
          principal contractors look at first.
        </p>

        <div className="mt-4 rounded-xl border border-emerald/30 bg-emerald-tint p-4">
          <h3 className="font-display text-base font-bold text-ink">Already sent back by a client?</h3>
          <p className="mt-1 text-sm leading-relaxed text-body">
            Paste their comments here as well. RamsDocs will use them as context for the review and record the
            rejection reason so future RAMS can pre-empt it.
          </p>
          <div className="mt-3 grid gap-2.5">
            <textarea
              rows={3}
              value={reviewerComments}
              onChange={(e) => setReviewerComments(e.target.value)}
              disabled={busy}
              maxLength={4000}
              placeholder="Paste the exact reviewer comments, e.g. 'Please expand access arrangements and confirm hot works permit/fire watch.'"
              className="w-full rounded-lg border border-emerald/30 bg-white px-3.5 py-2.5 text-[0.85rem] leading-relaxed text-ink placeholder:text-muted/70 focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald/25 disabled:opacity-60"
            />
            <input
              type="text"
              value={gatekeeper}
              onChange={(e) => setGatekeeper(e.target.value)}
              disabled={busy}
              maxLength={120}
              placeholder="Reviewer / client / principal contractor name (optional)"
              className="w-full rounded-lg border border-emerald/30 bg-white px-3.5 py-2.5 text-[0.85rem] text-ink placeholder:text-muted/70 focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald/25 disabled:opacity-60"
            />
          </div>
        </div>

        <div className="mt-4">
          <textarea
            rows={14}
            value={text}
            onChange={(e) => applyText(e.target.value)}
            onPaste={(e) => {
              const pasted = e.clipboardData.getData("text");
              const el = e.currentTarget;
              const next =
                text.slice(0, el.selectionStart ?? text.length) +
                pasted +
                text.slice(el.selectionEnd ?? text.length);
              if (next.length > MAX_CHARS) {
                e.preventDefault();
                applyText(next);
              }
            }}
            disabled={busy}
            placeholder="Paste the full RAMS text here — the scope, hazards and risk scoring, control measures, the method/sequence of work, the people and their competence, and the emergency arrangements. The more of the real document you paste, the more useful the review."
            className="w-full rounded-lg border border-line-2 bg-white px-3.5 py-2.5 font-mono text-[0.85rem] leading-relaxed text-ink placeholder:text-muted/70 focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald/25 disabled:opacity-60"
          />
          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[0.78rem]">
            <span className="text-muted">
              {charCount.toLocaleString("en-GB")} / {MAX_CHARS.toLocaleString("en-GB")} characters
              {trimmed && (
                <span className="ml-1.5 text-[#8a6400]">— trimmed to the first 120,000 characters</span>
              )}
            </span>
            {tooShort && charCount > 0 && (
              <span className="font-semibold text-muted">
                Paste at least {MIN_CHARS} characters to review
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
          <label
            className={`inline-flex items-center gap-1.5 rounded-full border border-line-2 px-3.5 py-1.5 text-[0.82rem] font-semibold text-ink ${
              busy ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-ink hover:bg-mist"
            }`}
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5-5 5 5" /><path d="M12 5v12" />
            </svg>
            {reading ? "Reading PDF…" : "Upload a PDF"}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              disabled={busy}
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />
          </label>
          <span className="text-[0.78rem] text-muted">
            PDF text is extracted in your browser — the file itself never leaves your device.
          </span>
        </div>
        {pdfError && (
          <p role="alert" className="mt-2 text-[0.82rem] font-semibold text-risk-veryhigh">
            {pdfError}
          </p>
        )}

        <p className="mt-4 text-[0.78rem] leading-relaxed text-muted">
          Sent securely for analysis only — not stored as a document, not used for training. Remove personal
          data you don&apos;t want sent.
        </p>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => void review()}
            disabled={tooShort || busy}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald px-7 py-3 text-[0.95rem] font-semibold text-white transition-colors hover:bg-emerald-deep disabled:cursor-not-allowed disabled:opacity-50"
          >
            {streaming ? "Reviewing…" : "Review my RAMS"}
            {!streaming && (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
              </svg>
            )}
          </button>
        </div>

        {error && (
          <div role="alert" className="mt-4 rounded-xl border border-risk-high/40 bg-[#fef0e6] px-4 py-3 text-[#b34e10]">
            <div className="text-[0.9rem] font-bold">Couldn&apos;t complete the review</div>
            <p className="mt-1 text-[0.85rem] leading-relaxed">{error}</p>
            <button
              type="button"
              onClick={() => void review()}
              disabled={busy}
              className="mt-2.5 rounded-full bg-ink px-4 py-2 text-[0.82rem] font-semibold text-white hover:bg-ink/85 disabled:opacity-50"
            >
              Try again
            </button>
          </div>
        )}
      </div>

      {streaming && (
        <div className="card shadow-soft p-6 print-hide">
          <div className="font-display text-base font-semibold text-ink">
            Reviewing like a PC document controller
            <ReviewingDots />
          </div>
          <p className="mt-1 text-sm text-muted">
            Working through the eight checks — this usually takes under a minute.
          </p>
          <div className="mt-4">
            <CheckProgress dimsArrived={dimsArrived} />
          </div>
        </div>
      )}
    </div>
  );
}
